import { useEffect, useState, type MouseEvent } from "react";
import styles from "./Tabs.module.scss";
import { type Person } from "@/shared/hooks/usePeople";
import { useUiStore } from "@/shared/stores/uiStore";
import { DotsIcon } from "../../assets/DotsIcon";
import { DropdownMenu } from "./DropdownMenu";
import { useToastStore } from "@/shared/stores/toastStore";
import { suppliersApi } from "@/shared/api/suppliers/api-suppliers";
import { useDraggableScroll } from "@/shared/hooks/useDraggableScroll";

interface TabsProps {
  selectedId: string;
  onSelect: (id: string) => void;
  people: Person[];
  loading?: boolean;
  refetchSuppliers: () => void;
}

export function Tabs({
  selectedId,
  onSelect,
  people,
  loading = false,
  refetchSuppliers,
}: TabsProps) {
  const { openModal, closeModal, isMasked } = useUiStore();
  const { showToast } = useToastStore();

  const {
    ref: tabsRef,
    isDragging,
    events,
  } = useDraggableScroll<HTMLDivElement>();

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuButtonRect, setMenuButtonRect] = useState<DOMRect | null>(null);

  const handleMenuClick = (e: React.MouseEvent, personId: string) => {
    e.stopPropagation();
    if (openMenuId === personId) {
      setOpenMenuId(null);
      setMenuButtonRect(null);
    } else {
      const button = e.currentTarget;
      setMenuButtonRect(button.getBoundingClientRect());
      setOpenMenuId(personId);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (openMenuId) {
        setOpenMenuId(null);
        setMenuButtonRect(null);
      }
    };
    const tabsElement = tabsRef.current;
    if (tabsElement && openMenuId) {
      tabsElement.addEventListener("scroll", handleScroll);
    }
    if (openMenuId) {
      window.addEventListener("scroll", handleScroll, true);
    }
    return () => {
      if (tabsElement) {
        tabsElement.removeEventListener("scroll", handleScroll);
      }
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openMenuId, tabsRef]);

  const confirmLeave = async (supplierId: string) => {
    try {
      await suppliersApi.leaveSupplier(supplierId);
      showToast("Вы успешно вышли из кабинета", "success");
      refetchSuppliers();
    } catch (error) {
      console.error("Ошибка выхода из кабинета:", error);
      showToast("Не удалось выйти из кабинета", "error");
    } finally {
      closeModal();
    }
  };

  const handleLeaveRequest = (personId: string) => {
    const person = people.find((p) => p.id === personId);
    if (person) {
      openModal("CONFIRMATION", {
        message: "Вы уверены, что хотите покинуть кабинет?",
        onConfirm: () => confirmLeave(person.id),
      });
    }
    setOpenMenuId(null);
  };

  const handleClick = (e: MouseEvent, personId: string) => {
    if (
      e.detail > 0 &&
      Math.abs(e.movementX) < 2 &&
      Math.abs(e.movementY) < 2
    ) {
      if (!isDragging) {
        onSelect(personId);
      }
    }
  };

  useEffect(() => {
    if (loading || people.length === 0) return;

    const activeTab = tabsRef.current?.querySelector(
      `[data-id="${selectedId}"]`,
    ) as HTMLElement;
    if (activeTab && tabsRef.current) {
      const containerWidth = tabsRef.current.offsetWidth;
      const tabLeft = activeTab.offsetLeft;
      const tabWidth = activeTab.offsetWidth;
      const scrollPosition = tabLeft - containerWidth / 2 + tabWidth / 2;

      tabsRef.current.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: "smooth",
      });
    }
  }, [selectedId, people, loading, tabsRef]);

  return (
    <>
      <div className={styles.tabsContainer}>
        <div className={styles.tabsWrapper}>
          <button
            className={styles.addTokenButton}
            disabled={loading ? true : false}
            onClick={() =>
              openModal("TOKEN", { onTokenAdded: refetchSuppliers })
            }
            title="Добавить токен доступа"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 5V19M5 12H19"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {loading ? (
            <div ref={tabsRef} className={styles.tabs}>
              <div className={styles.emptyTabs}>
                <span className={styles.emptyMessage}>Загрузка...</span>
              </div>
            </div>
          ) : (
            <div
              ref={tabsRef}
              className={`${styles.tabs} ${isDragging ? styles.dragging : ""}`}
              {...events}
              style={{
                cursor: isDragging ? "grabbing" : "grab",
              }}
            >
              {people.length === 0 ? (
                <div className={styles.emptyTabs}>
                  <span className={styles.emptyMessage}>
                    Никого не подключено
                  </span>
                </div>
              ) : (
                people.map((person) => {
                  const tabClassName = `${styles.tab} ${selectedId === person.id ? styles.active : ""}`;

                  return (
                    <div
                      key={person.id}
                      data-id={person.id}
                      data-token-id={person.token_id}
                      className={tabClassName}
                      onClick={(e) => handleClick(e, person.id)}
                    >
                      <div className={styles.tabContent}>
                        <span
                          className={`${styles.personName} ${isMasked ? styles.maskedText : ""}`}
                        >
                          {person.name}
                        </span>
                        <span className={styles.personStatus}>
                          {person.status}
                        </span>
                      </div>
                      <div className={styles.tabOptions}>
                        <div
                          onClick={(e) => handleMenuClick(e, person.id)}
                          className={styles.menuButton}
                        >
                          <DotsIcon />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
          {openMenuId && menuButtonRect && (
            <DropdownMenu
              buttonRect={menuButtonRect}
              onClose={() => setOpenMenuId(null)}
              onLeave={() => handleLeaveRequest(openMenuId)}
            />
          )}
        </div>
      </div>
    </>
  );
}
